import React from 'react'
import { useSelector } from 'react-redux'
import { Box, EmptyState, EmptyStateTitle, Text } from '@indeed/ifl-components'

import { selectVenueName } from 'syft-acp-core/store/employer-venue-search/selectors'

import type { EmployerAccountsVenueResultsProps } from './EmployerAccountsList.types'

const useVenueLabel = (venueID: string) => {
  const name = useSelector(state => selectVenueName(state, venueID))

  return name ? `${venueID}: ${name}` : `venue ${venueID}`
}

export const EmployerAccountsVenueSummary = ({
  venueID,
  isSearching = false,
  total,
}: EmployerAccountsVenueResultsProps & Readonly<{ total: number }>) => {
  const venueLabel = useVenueLabel(venueID)
  const managers = `${total.toLocaleString('en-GB')} manager${total === 1 ? '' : 's'}`

  return (
    <Box sx={{ marginBlockEnd: 4 }}>
      <Text as="p">
        <strong>{managers}</strong> {isSearching ? 'at' : 'linked to'} <strong>{venueLabel}</strong>
        {isSearching ? ' match your search' : ''}. Sorted by fewest locations first, so site-level contacts
        are at the top.
      </Text>
    </Box>
  )
}

export const EmployerAccountsVenueEmptyState = ({
  venueID,
  isSearching = false,
}: EmployerAccountsVenueResultsProps) => {
  const venueLabel = useVenueLabel(venueID)

  return (
    <EmptyState size="sm">
      <EmptyStateTitle>
        {isSearching
          ? `No managers at ${venueLabel} match your search`
          : `No active location-level managers are linked to ${venueLabel}`}
      </EmptyStateTitle>
      {isSearching
        ? 'Clear the Name or E-mail filter to see everyone linked to this venue.'
        : 'Main accounts and company-wide managers can still see every venue. Clear the Venue filter to see them.'}
    </EmptyState>
  )
}
